import { Dialog, DialogContent, DialogTitle } from '@crowdex/ui/components/dialog';
import { Input } from '@crowdex/ui/components/input';
import { cn } from '@crowdex/ui/lib/utils';
import { Archive, Bell, CheckCheck, Plus, Search, X } from 'lucide-react';
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { CrowdexMark } from '@/components/crowdex-mark';
import { useAppSidebarSide } from '@/components/desktop-shell/sidebar-side-toggle';
import { AccountAvatar, useAccountsToReconnect } from '@/components/sidebar/account-menu';
import { failed, useConversationActions } from '@/components/sidebar/conversation-actions';
import { FavoriteTiles } from '@/components/sidebar/favorite-tiles';
import { setSearchFilter } from '@/components/sidebar/search-dialog';
import {
  type ShellNavigation,
  ShellNavigationProvider,
  useShellNavigation,
} from '@/components/sidebar/shell-navigation';
import { useSidebarView } from '@/components/sidebar/sidebar-preferences';
import { RowStateIcon } from '@/components/sidebar/sidebar-rows';
import { SidebarViewSheetContent } from '@/components/sidebar/view-menu';
import { WorkspaceBody } from '@/components/sidebar/workspace-body';
import { useNotifications } from '@/features/notifications/notifications';
import { PREVIEW_ACCOUNTS_TO_RECONNECT } from '@/lib/desktop-shell/preview-shell-data';
import { formatAge, type SidebarRow } from '@/lib/desktop-shell/sidebar-model';
import { useDesktopShellStoreApi } from '@/lib/desktop-shell/store-context';
import { useLibraryPage, useRouter } from '@/lib/navigation';
import { useSwipeAction } from './use-swipe-action';

/** The drawer's width: the conversation stays in sight beside it. */
export const DRAWER_WIDTH = 312;
/** Its width on screen: on the smallest phones, the conversation keeps a strip to tap. */
export const DRAWER_OFFSET = `min(${DRAWER_WIDTH}px, 100vw - 3.5rem)`;

/**
 * The phone's drawer (mobile 2), in place of the tab bar: the crow, search and avatar
 * (Settings), the four favourites, Needs you, the assistants and the conversations (their View
 * in a sheet, 2c), and New conversation. A long press on a row offers its actions (19); swiped right it is read, left
 * archived.
 */
export function MobileDrawer({
  open,
  account,
  onLeave,
  onOpenNotifications,
  onOpenSearch,
}: {
  open: boolean;
  account: { name: string; email: string };
  /** Closes the drawer (and what it pushed), then does what was chosen there. */
  onLeave: (then?: () => void) => void;
  onOpenNotifications: () => void;
  onOpenSearch: () => void;
}) {
  const navigation = useShellNavigation();
  const store = useDesktopShellStoreApi();
  const router = useRouter();
  const notifications = useNotifications();
  const accountsToReconnect = useAccountsToReconnect(!navigation.preview);
  // An account to reconnect puts an amber dot on the avatar (the boards' preview has one).
  const reconnect = navigation.preview ? PREVIEW_ACCOUNTS_TO_RECONNECT : accountsToReconnect;
  const { view, setView } = useSidebarView();
  const [viewOpen, setViewOpen] = useState(false);
  const [pressed, setPressed] = useState<SidebarRow | null>(null);
  // An app open full screen with its own navigation: "Conversations | <App>" (as the sidebar).
  const app = useAppSidebarSide(useLibraryPage().open, 'drawer');
  // An item of the app's navigation shows in the app: the drawer gets out of its way.
  const appNavigation = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const element = appNavigation.current;
    if (element === null) return;
    const onClick = (event: MouseEvent) => {
      if (event.target instanceof Element && event.target.closest('[data-sidebar-item]') !== null)
        onLeave();
    };
    element.addEventListener('click', onClick);
    return () => element.removeEventListener('click', onClick);
  }, [onLeave]);
  // What the drawer opens closes it; a conversation opens on its chat, not the panel before.
  const closing = useMemo<ShellNavigation>(() => {
    const onChat = (then: () => void) => () => {
      store.getState().setSidePanelOpen(false);
      then();
    };
    return {
      ...navigation,
      openThread: (thread) => onLeave(onChat(() => navigation.openThread(thread))),
      openConversation: (id) => onLeave(onChat(() => navigation.openConversation(id))),
      newTask: () => onLeave(onChat(navigation.newTask)),
    };
  }, [navigation, onLeave, store]);
  return (
    <ShellNavigationProvider value={closing}>
      <aside
        aria-hidden={!open || undefined}
        aria-label="Drawer"
        className="mobile-drawer absolute inset-y-0 left-0 z-10 flex flex-col bg-desktop-sidebar pb-3"
        inert={!open || undefined}
        style={{ width: DRAWER_OFFSET }}
      >
        <header className="flex h-16 shrink-0 items-center gap-2 px-6 pt-2 max-[359px]:px-4">
          <h1 className="flex-1">
            <CrowdexMark className="h-8 w-auto" label="Crowdex" />
          </h1>
          <button
            aria-label={
              (notifications?.unread ?? 0) > 0
                ? `Notifications, ${notifications?.unread} unread`
                : 'Notifications'
            }
            className="relative flex size-11 shrink-0 items-center justify-center rounded-control bg-control/70 active:bg-control"
            onClick={onOpenNotifications}
            type="button"
          >
            <Bell className="size-5" />
            {(notifications?.unread ?? 0) > 0 && (
              <span className="absolute top-2.5 right-2.5 size-2 rounded-full bg-attention" />
            )}
          </button>
          <button
            aria-label="Search"
            className="flex size-11 shrink-0 items-center justify-center rounded-control bg-control/70 active:bg-control"
            onClick={onOpenSearch}
            type="button"
          >
            <Search className="size-5" />
          </button>
          <button
            aria-label="Settings"
            className="shrink-0 rounded-full"
            onClick={() => onLeave(() => router.push('/settings'))}
            type="button"
          >
            <AccountAvatar
              attention={reconnect.length > 0}
              label={account.name || account.email}
              size="lg"
            />
          </button>
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pb-4">
          <div className="pt-2 pb-4">
            <FavoriteTiles density="drawer" onLeave={onLeave} />
          </div>
          <hr className="mx-2 mb-3 border-hairline" />
          {app.toggle !== null && (
            <div className="mb-3" data-sidebar-side-toggle>
              {app.toggle}
            </div>
          )}
          <div ref={appNavigation}>{app.navigation}</div>
          {!app.appShown && (
            <WorkspaceBody
              density="drawer"
              onLongPress={setPressed}
              onOpenView={() => setViewOpen(true)}
              wrapRow={(row, node) => <SwipeRow row={row}>{node}</SwipeRow>}
            />
          )}
        </div>
        <div className="flex shrink-0 flex-col gap-3 px-4 pt-2">
          <button
            className="flex h-12 w-full items-center gap-3 rounded-2xl bg-control/70 px-4 text-left text-foreground active:bg-control"
            onClick={closing.newTask}
            type="button"
          >
            <Plus className="size-5 shrink-0 text-muted-foreground" />
            <span className="min-w-0 flex-1 truncate">New conversation</span>
          </button>
        </div>
      </aside>
      <Dialog onOpenChange={setViewOpen} open={viewOpen}>
        <DialogContent
          aria-describedby={undefined}
          className="top-auto bottom-0 left-0 flex max-h-[85dvh] w-full max-w-full translate-x-0 translate-y-0 flex-col gap-3 overflow-y-auto rounded-b-none rounded-t-window bg-background p-4 pb-[max(1rem,env(safe-area-inset-bottom))] sm:max-w-full"
          data-sheet
          showCloseButton={false}
        >
          <span aria-hidden className="mx-auto h-1 w-10 rounded-full bg-control" />
          <div className="flex items-center">
            <button
              aria-label="Close"
              className="flex size-9 items-center justify-center rounded-full"
              onClick={() => setViewOpen(false)}
              type="button"
            >
              <X className="size-5" />
            </button>
            <DialogTitle className="flex-1 text-center font-semibold text-base">View</DialogTitle>
            <span aria-hidden className="size-9" />
          </div>
          <SidebarViewSheetContent
            archivedCount={null}
            onChange={setView}
            onShowArchived={() => {
              setViewOpen(false);
              setSearchFilter('archived');
              onOpenSearch();
            }}
            {...(navigation.addRepository === undefined
              ? {}
              : {
                  onAddRepository: () => {
                    setViewOpen(false);
                    onLeave(navigation.addRepository);
                  },
                })}
            view={view}
          />
        </DialogContent>
      </Dialog>
      {pressed !== null && (
        <ConversationActionSheet onClose={() => setPressed(null)} row={pressed} />
      )}
    </ShellNavigationProvider>
  );
}

/** A long press on a conversation (19): the row lifted above its actions in a sheet. */
function ConversationActionSheet({ row, onClose }: { row: SidebarRow; onClose: () => void }) {
  const [renaming, setRenaming] = useState(false);
  const [title, setTitle] = useState(row.thread.title);
  const navigation = useShellNavigation();
  const notifications = useNotifications();
  // What a swipe of its row does, as SwipeRow does it.
  const swipes = [
    navigation.archiveConversation !== undefined && 'left: Archive',
    swipeReads(row) && notifications !== null && 'right: Mark as read',
  ].filter((swipe) => swipe !== false);
  const { actions, confirmDelete } = useConversationActions(row.thread, {
    running: row.state === 'running',
    surface: 'phone',
    onRename: () => setRenaming(true),
  });
  return (
    <>
      <Dialog onOpenChange={(open) => !open && onClose()} open>
        <DialogContent
          aria-describedby={undefined}
          className="top-auto bottom-0 left-0 flex w-full max-w-full translate-x-0 translate-y-0 flex-col gap-3 rounded-none border-0 bg-transparent p-4 pb-[max(1rem,env(safe-area-inset-bottom))] shadow-none sm:max-w-full"
          data-sheet
          showCloseButton={false}
        >
          <div className="flex items-center gap-3 rounded-card bg-card px-4 py-3">
            <RowStateIcon className="size-6" state={row.state} />
            <div className="min-w-0 flex-1">
              <DialogTitle className="truncate font-semibold">{row.thread.title}</DialogTitle>
              <p className="type-meta truncate text-muted-foreground">
                {row.thread.project} · {formatAge(row.thread.updatedAt)}
              </p>
            </div>
          </div>
          {renaming ? (
            <form
              className="flex gap-2 rounded-card bg-card p-3"
              onSubmit={(event) => {
                event.preventDefault();
                const next = title.trim();
                onClose();
                if (next !== '' && next !== row.thread.title)
                  void navigation.renameConversation?.(row.thread, next);
              }}
            >
              <Input
                aria-label="Conversation name"
                className="h-11 min-w-0 flex-1 rounded-xl bg-background px-3"
                onChange={(event) => setTitle(event.target.value)}
                ref={(input) => input?.focus()}
                value={title}
              />
              <button
                className="rounded-control bg-primary px-4 font-medium text-primary-foreground"
                type="submit"
              >
                Save
              </button>
            </form>
          ) : (
            <ul className="overflow-hidden rounded-card bg-card py-1">
              {actions.map((action) => {
                const Icon = action.icon;
                return (
                  <li key={action.id}>
                    {action.separated && <hr className="mx-4 my-1 border-hairline" />}
                    <button
                      className={cn(
                        'flex min-h-12 w-full items-center gap-4 px-5 text-left active:bg-accent/60 disabled:opacity-40',
                        action.destructive && 'text-destructive',
                      )}
                      onClick={() => {
                        action.run?.();
                        if (action.id !== 'rename' && action.id !== 'delete') onClose();
                      }}
                      type="button"
                    >
                      <Icon className="size-5" />
                      {action.label}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          {swipes.length > 0 && (
            <p className="type-meta text-center text-muted-foreground">
              Or swipe a card {swipes.join(' · ')}
            </p>
          )}
        </DialogContent>
      </Dialog>
      {confirmDelete}
    </>
  );
}

/** Whether a swipe right reads the row: it is unread, or waits on the user. */
function swipeReads(row: SidebarRow): boolean {
  return row.thread.unread === true || row.waitingSince !== null;
}

/**
 * A row of the drawer that a swipe reads (right) or archives (left), over the mark it uncovers.
 */
export function SwipeRow({ row, children }: { row: SidebarRow; children: ReactNode }) {
  const notifications = useNotifications();
  const navigation = useShellNavigation();
  const ref = useRef<HTMLDivElement>(null);
  useSwipeAction(
    ref,
    swipeReads(row) && notifications !== null
      ? () => notifications.markRead({ threadId: row.thread.id })
      : null,
    true,
    navigation.archiveConversation === undefined
      ? null
      : () => void navigation.archiveConversation?.(row.thread).catch(failed('archived')),
  );
  return (
    <div className="relative overflow-hidden rounded-xl">
      <div
        aria-hidden
        className="absolute inset-0 flex items-center justify-between rounded-xl px-4"
      >
        <span className="flex items-center gap-2 text-running opacity-[var(--swipe-progress,0)]">
          <CheckCheck className="size-4" /> Read
        </span>
        <span className="flex items-center gap-2 text-destructive opacity-[var(--swipe-left-progress,0)]">
          Archive <Archive className="size-4" />
        </span>
      </div>
      <div
        className="relative bg-desktop-sidebar transition-transform duration-200 ease-out"
        ref={ref}
      >
        {children}
      </div>
    </div>
  );
}
